import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import {
  MessageCircle, Send, RefreshCw, CheckCheck, Check,
  X, AlertCircle, Inbox, Phone, ChevronLeft,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Conversation {
  phone: string;
  contact_name: string | null;
  claim_id: number | null;
  slate_id: number | null;
  school_name: string | null;
  total_messages: number;
  unread_count: number;
  failed_count: number;
  last_message_at: string;
  last_message: string | null;
  last_direction: 'inbound' | 'outbound';
  last_status: string;
}

interface Message {
  id: number;
  phone: string;
  contact_name: string | null;
  direction: 'inbound' | 'outbound';
  template_name: string | null;
  message_text: string | null;
  status: 'sent' | 'delivered' | 'read' | 'failed' | 'received';
  failure_reason: string | null;
  is_read: number;
  created_at: string;
}

interface Template {
  status: string;
  template_name: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatTime = (dt: string) => {
  const d = new Date(dt);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  return isToday
    ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const STATUS_META: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  failed:    { icon: <AlertCircle size={12} />, label: 'Failed',    color: 'text-red-400' },
  read:      { icon: <CheckCheck size={12} />,  label: 'Read',      color: 'text-blue-300' },
  delivered: { icon: <CheckCheck size={12} />,  label: 'Delivered', color: 'text-gray-300' },
  sent:      { icon: <Check size={12} />,       label: 'Sent',      color: 'text-gray-300' },
};

const StatusBadge = ({ status }: { status: string }) => {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <span className={`flex items-center gap-0.5 ${meta.color}`}>
      {meta.icon}
      <span className="text-xs">{meta.label}</span>
    </span>
  );
};

// ── Send Template Modal ───────────────────────────────────────────────────────

const SendTemplateModal = ({
  phone,
  claimId,
  contactName,
  templates,
  onClose,
  onSent,
}: {
  phone: string;
  claimId: number | null;
  contactName: string;
  templates: Template[];
  onClose: () => void;
  onSent: () => void;
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!selectedTemplate) return;
    setIsSending(true);
    try {
      const res = await apiRequest('/api/whatsapp-inbox/send', {
        method: 'POST',
        body: JSON.stringify({
          phone,
          template_name: selectedTemplate,
          claim_id: claimId,
          contact_name: contactName,
        }),
      });
      if (res.success) {
        showToast('Template sent successfully', 'success');
        onSent();
        onClose();
      } else {
        showToast(res.message || 'Failed to send', 'error');
      }
    } catch {
      showToast('Failed to send template', 'error');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">Send Template</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-1">To: <span className="font-medium text-gray-800 dark:text-gray-200">{phone}</span></p>
        {contactName && <p className="text-sm text-gray-500 mb-4">{contactName}</p>}
        <select
          value={selectedTemplate}
          onChange={e => setSelectedTemplate(e.target.value)}
          className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm mb-4 bg-white dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a template...</option>
          {templates.map(t => (
            <option key={t.template_name} value={t.template_name}>
              {t.status} — {t.template_name}
            </option>
          ))}
        </select>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={handleSend}
            disabled={!selectedTemplate || isSending}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Send size={14} /> {isSending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const WhatsAppInbox: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [category, setCategory] = useState<'claims' | 'school_leads' | 'bot_leads'>('claims');
  const [filter, setFilter] = useState<'all' | 'unread' | 'replied' | 'failed'>('all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [repliedCount, setRepliedCount] = useState(0);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [retryingMsgId, setRetryingMsgId] = useState<number | null>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  const fetchConversations = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ filter, limit: '100', category });
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      if (templateFilter) params.set('template', templateFilter);
      const res = await apiRequest(`/api/whatsapp-inbox/conversations?${params}`, { method: 'GET' });
      if (res.success) setConversations(res.data?.conversations || []);
    } catch {
      showToast('Failed to load conversations', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredConversations = search.trim()
    ? conversations.filter(c =>
        c.phone.includes(search.trim()) ||
        (c.contact_name || '').toLowerCase().includes(search.trim().toLowerCase()) ||
        (c.school_name || '').toLowerCase().includes(search.trim().toLowerCase())
      )
    : conversations;

  const fetchThread = async (phone: string) => {
    try {
      setIsLoadingThread(true);
      const res = await apiRequest(`/api/whatsapp-inbox/conversations/${encodeURIComponent(phone)}`, { method: 'GET' });
      if (res.success) setMessages(res.data?.messages || []);
    } catch {
      showToast('Failed to load messages', 'error');
    } finally {
      setIsLoadingThread(false);
    }
  };

  const fetchBadge = async () => {
    const res = await apiRequest(`/api/whatsapp-inbox/unread-count?category=${category}`, { method: 'GET' });
    if (res.success) {
      setUnreadCount(res.data?.unread || 0);
      setFailedCount(res.data?.failed || 0);
      setRepliedCount(res.data?.replied || 0);
    }
  };

  const fetchTemplates = async () => {
    const res = await apiRequest(`/api/whatsapp-inbox/templates?category=${category}`, { method: 'GET' });
    if (res.success) setTemplates(res.data?.templates || []);
  };

  const sendReply = async () => {
    if (!selectedPhone || !replyText.trim() || isSendingReply) return;
    setIsSendingReply(true);
    try {
      const res = await apiRequest('/api/whatsapp-inbox/reply', {
        method: 'POST',
        body: JSON.stringify({ phone: selectedPhone, message: replyText.trim(), claim_id: selectedConv?.claim_id || null }),
      });
      if (res.success) {
        setReplyText('');
        fetchThread(selectedPhone);
        fetchBadge();
      } else {
        showToast(res.message || 'Failed to send reply', 'error');
      }
    } catch {
      showToast('Failed to send reply', 'error');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleRetryTemplate = async (msgId: number, phone: string, claimId: number | null) => {
    setRetryingMsgId(msgId);
    try {
      const res = await apiRequest('/api/whatsapp-inbox/retry-template', {
        method: 'POST',
        body: JSON.stringify({ phone, claim_id: claimId || null }),
      });
      if (res.success) {
        showToast(res.data?.message || 'Template retried successfully', 'success');
        if (selectedPhone) fetchThread(selectedPhone);
      } else {
        showToast(res.message || 'Retry failed', 'error');
      }
    } catch {
      showToast('Retry failed', 'error');
    } finally {
      setRetryingMsgId(null);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [filter, fromDate, toDate, templateFilter, category]);

  useEffect(() => {
    setTemplateFilter('');
    setSelectedPhone(null);
    setSelectedConv(null);
    fetchTemplates();
    fetchBadge();
  }, [category]);

  useEffect(() => {
    fetchTemplates();
    fetchBadge();
  }, []);

  // Immediately apply phone from URL when searchParams changes
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    if (!phoneParam) return;
    setSelectedPhone(phoneParam);
    setSelectedConv(null);
  }, [searchParams]);

  // Once conversations load, enrich the selected phone with full conv details
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    if (!phoneParam || !conversations.length) return;
    const match = conversations.find(c => c.phone === phoneParam);
    if (match) selectConversation(match);
  }, [conversations]);

  useEffect(() => {
    if (selectedPhone) fetchThread(selectedPhone);
  }, [selectedPhone]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectConversation = (conv: Conversation) => {
    setSelectedPhone(conv.phone);
    setSelectedConv(conv);
    // Mark as read in local state
    setConversations(prev =>
      prev.map(c => c.phone === conv.phone ? { ...c, unread_count: 0 } : c)
    );
  };

  return (
    <div className="h-full flex bg-gray-50 dark:bg-gray-900">
      {/* Left — Conversation List */}
      <div className={`${selectedPhone ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          {/* Category tabs */}
          <div className="flex mb-3 border-b border-gray-200 dark:border-gray-700">
            {([
              { key: 'claims', label: 'Claim Templates' },
              { key: 'school_leads', label: 'School Leads' },
              { key: 'bot_leads', label: 'Bot Leads' },
            ] as const).map(tab => (
              <button
                key={tab.key}
                onClick={() => { setCategory(tab.key); setFilter('all'); }}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px ${
                  category === tab.key
                    ? 'border-green-600 text-green-600 dark:text-green-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageCircle size={18} className="text-green-600" /> WhatsApp Inbox
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={fetchConversations} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <RefreshCw size={14} className="text-gray-500" />
              </button>
            </div>
          </div>
          {/* Filter tabs */}
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {([
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'New', count: unreadCount },
              { key: 'replied', label: 'Replied', count: repliedCount },
              { key: 'failed', label: 'Failed', count: failedCount },
            ] as const).map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`flex-1 text-xs py-1 rounded-md transition-colors flex items-center justify-center gap-1 ${
                  filter === f.key
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm font-medium'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {f.label}
                {'count' in f && f.count > 0 && (
                  <span className={`text-xs rounded-full px-1 min-w-[16px] text-center leading-4 ${
                    f.key === 'failed' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                  }`}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search + advanced filters */}
          <div className="mt-3 space-y-2">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search phone, name or school..."
              className="w-full px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <select
              value={templateFilter}
              onChange={e => setTemplateFilter(e.target.value)}
              className="w-full px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">All templates</option>
              {templates.map(t => (
                <option key={t.template_name} value={t.template_name}>{t.status}</option>
              ))}
            </select>
            <div className="flex gap-1.5 items-center">
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <span className="text-gray-400 text-xs">–</span>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              {(fromDate || toDate || templateFilter) && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); setTemplateFilter(''); }}
                  className="text-xs text-red-500 hover:text-red-700 whitespace-nowrap"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-8 text-center">
              <Inbox size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">{search ? 'No matches found' : 'No conversations'}</p>
            </div>
          ) : (
            filteredConversations.map(conv => (
              <button
                key={conv.phone}
                onClick={() => selectConversation(conv)}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  selectedPhone === conv.phone ? 'bg-green-50 dark:bg-green-900/20 border-l-2 border-l-green-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {conv.contact_name || conv.phone}
                      </span>
                      {conv.unread_count > 0 && (
                        <span className="flex-shrink-0 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                      {conv.failed_count > 0 && (
                        <AlertCircle size={14} className="flex-shrink-0 text-red-500" />
                      )}
                    </div>
                    {conv.school_name && (
                      <p className="text-xs text-gray-500 truncate">{conv.school_name}</p>
                    )}
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-xs text-gray-400 truncate">
                        {conv.last_direction === 'outbound' ? '→ ' : '← '}
                        {conv.last_message || conv.last_status}
                      </span>
                      {conv.last_direction === 'outbound' && conv.last_status === 'failed' && (
                        <span className="flex-shrink-0 text-xs text-red-500 font-medium">Failed</span>
                      )}
                      {conv.last_direction === 'outbound' && conv.last_status === 'delivered' && (
                        <CheckCheck size={11} className="flex-shrink-0 text-gray-400" />
                      )}
                      {conv.last_direction === 'outbound' && conv.last_status === 'read' && (
                        <CheckCheck size={11} className="flex-shrink-0 text-blue-500" />
                      )}
                      {conv.last_direction === 'outbound' && conv.last_status === 'sent' && (
                        <Check size={11} className="flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{formatTime(conv.last_message_at)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right — Thread */}
      <div className={`${selectedPhone ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
        {!selectedPhone ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle size={48} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">Select a conversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedPhone(null)} className="md:hidden p-1">
                  <ChevronLeft size={18} />
                </button>
                <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <Phone size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {selectedConv?.contact_name || selectedPhone}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedPhone}
                    {selectedConv?.school_name && ` · ${selectedConv.school_name}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSendModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
              >
                <Send size={14} /> Send Template
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {isLoadingThread ? (
                <div className="text-center text-sm text-gray-400 py-8">Loading...</div>
              ) : messages.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-8">No messages yet</div>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md rounded-2xl px-4 py-2.5 ${
                        msg.direction === 'outbound'
                          ? msg.status === 'failed'
                            ? 'bg-red-600 text-white rounded-br-sm'
                            : 'bg-green-600 text-white rounded-br-sm'
                          : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm rounded-bl-sm'
                      }`}
                    >
                      {msg.template_name && (
                        <p className={`text-xs mb-1 font-medium ${msg.direction === 'outbound' ? 'text-green-100' : 'text-gray-400'}`}>
                          Template: {msg.template_name}
                        </p>
                      )}
                      <p className="text-sm leading-relaxed">{msg.message_text || msg.template_name}</p>
                      {msg.status === 'failed' && (
                        <div className="mt-1.5 space-y-1">
                          <p className="text-xs text-red-200 flex items-center gap-1 bg-red-700/40 rounded px-2 py-1">
                            <AlertCircle size={10} className="flex-shrink-0" />
                            {msg.failure_reason || 'Message failed to send'}
                          </p>
                          {msg.failure_reason?.includes('Meta has restricted') && selectedConv && (() => {
                            const hoursAgo = (Date.now() - new Date(msg.created_at).getTime()) / (1000 * 60 * 60);
                            if (hoursAgo >= 48) return (
                              <button
                                disabled={retryingMsgId === msg.id}
                                onClick={() => handleRetryTemplate(msg.id, selectedConv.phone, selectedConv.claim_id)}
                                className="w-full text-xs font-medium px-2 py-1 rounded bg-white/20 hover:bg-white/30 text-white disabled:opacity-50 transition-colors"
                              >
                                {retryingMsgId === msg.id ? 'Retrying...' : '↻ Retry'}
                              </button>
                            );
                            return (
                              <p className="text-xs text-red-200 opacity-75 px-1">
                                Retry available after {Math.ceil(48 - hoursAgo)}h
                              </p>
                            );
                          })()}
                        </div>
                      )}
                      <div className={`flex items-center justify-end gap-1.5 mt-1.5 ${msg.direction === 'outbound' ? 'text-green-100' : 'text-gray-400'}`}>
                        <span className="text-xs opacity-75">{formatTime(msg.created_at)}</span>
                        {msg.direction === 'outbound' && <StatusBadge status={msg.status} />}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={threadEndRef} />
            </div>

            {/* Reply input */}
            <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={replyInputRef}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Type a reply... (only works within 24h of user's last message)"
                  rows={2}
                  className="flex-1 resize-none border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <button
                  onClick={sendReply}
                  disabled={!replyText.trim() || isSendingReply}
                  className="p-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <Send size={16} />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Enter to send · Shift+Enter for new line · For older conversations use <button onClick={() => setShowSendModal(true)} className="text-green-600 hover:underline">Send Template</button></p>
            </div>
          </>
        )}
      </div>

      {/* Send Template Modal */}
      {showSendModal && selectedPhone && (
        <SendTemplateModal
          phone={selectedPhone}
          claimId={selectedConv?.claim_id || null}
          contactName={selectedConv?.contact_name || ''}
          templates={templates}
          onClose={() => setShowSendModal(false)}
          onSent={() => { fetchThread(selectedPhone); fetchConversations(); fetchBadge(); }}
        />
      )}
    </div>
  );
};

export default WhatsAppInbox;
